import { ArrowRight, Landmark, PiggyBank, Target, TrendingUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { T, FONT, SERIF, mix } from '../theme';
import { ChecklistCard, Eyebrow, H1, Stage, Sub } from '../primitives';
import type { ResultsVM } from '../../formatters/deckContent';

// Illustrative ways the added value could be directed — possibilities, not promises.
// "Room to spend more" is lifestyle framing — dropped when the advisor opted the
// lifestyle presentation out for this run (vm.showLifestyle).
const USES: [LucideIcon, string, string][] = [
  [Landmark, 'A lasting legacy', 'Pass more to your heirs, much of it free of income tax.'],
  [Target, 'Freedom to give', 'Fund family gifts, education, or causes you care about, on your terms.'],
  [PiggyBank, 'A deeper safety net', 'Hold more in reserve for healthcare, long-term care, or simply peace of mind.'],
  [TrendingUp, 'Room to spend more', 'Support a higher lifestyle today, knowing the plan still holds.'],
];
const LIFESTYLE_USE_TITLE = 'Room to spend more';

interface Props {
  vm: ResultsVM;
}

/** Act 5 — Results: hero total, heirs row, changes tiles, uses, checklists. */
export function ActResults({ vm }: Props) {
  return (
    <Stage>
      <Eyebrow>What it achieves</Eyebrow>
      <H1>{vm.totalPlanValue ? `Your plan delivers ${vm.totalPlanValue} in total value` : 'What your plan achieves'}</H1>
      <Sub>Here's the headline outcome, what it means for your heirs, and the changes behind it.</Sub>

      {/* TIER 1 — hero: Total Plan Value, the main outcome */}
      {vm.totalPlanValue && (
        <div
          style={{
            background: T.teal,
            borderRadius: 16,
            padding: '32px',
            color: '#fff',
            textAlign: 'center',
            marginBottom: 16,
            boxShadow: `0 8px 28px ${mix(T.teal, 30)}`,
          }}
        >
          <div
            style={{
              fontFamily: FONT,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              opacity: 0.85,
              marginBottom: 10,
            }}
          >
            Total Plan Value
          </div>
          <div style={{ fontFamily: SERIF, fontSize: 64, fontWeight: 600, lineHeight: 1, marginBottom: 12 }}>
            {vm.totalPlanValue}
          </div>
          {vm.addedChip && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: 'rgba(255,255,255,.18)',
                borderRadius: 24,
                padding: '6px 16px',
              }}
            >
              <span style={{ fontSize: 13.5, opacity: 0.92, fontWeight: 600 }}>{vm.addedChip}</span>
            </div>
          )}
          <div style={{ fontSize: 12.5, opacity: 0.75, marginTop: 10 }}>
            After-Tax Cashflow + Savings at End
          </div>
        </div>
      )}

      {/* TIER 2 — supporting metric: Assets to Heirs */}
      {vm.heirs && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: T.card,
            border: `1px solid ${T.grayLine}`,
            borderRadius: 12,
            padding: '20px 24px',
            marginBottom: 30,
            boxShadow: '0 2px 8px rgba(27,42,74,.05)',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: T.gray,
                marginBottom: 4,
              }}
            >
              Assets available to heirs
            </div>
            <div style={{ fontSize: 13.5, color: T.gray }}>{vm.heirs.sub}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span style={{ fontSize: 34, fontWeight: 700, color: T.navy }}>{vm.heirs.after}</span>
            {vm.heirs.delta && (
              <span style={{ fontSize: 15, fontWeight: 700, color: T.teal }}>{vm.heirs.delta}</span>
            )}
          </div>
        </div>
      )}

      {/* TIER 3 — secondary: the changes behind it */}
      {vm.changes.length > 0 && (
        <>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              color: T.gray,
              marginBottom: 12,
            }}
          >
            The changes behind it
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(3, vm.changes.length)},1fr)`,
              gap: 14,
              marginBottom: 32,
            }}
          >
            {vm.changes.map((c) => (
              <div
                key={c.label}
                style={{ background: '#F4F6F8', border: `1px solid ${T.grayLine}`, borderRadius: 10, padding: '16px 18px' }}
              >
                <div style={{ fontSize: 12, color: T.gray, marginBottom: 8 }}>{c.label}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 13, color: T.gray }}>{c.before}</span>
                  <ArrowRight size={13} color="var(--color-gray)" />
                  <span style={{ fontSize: 19, fontWeight: 700, color: c.improved ? T.teal : T.navy }}>{c.after}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* what the added value makes possible */}
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: T.gray,
          marginBottom: 14,
        }}
      >
        Ways to put it to work
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 30 }}>
        {USES.filter(([, title]) => vm.showLifestyle || title !== LIFESTYLE_USE_TITLE).map(([Icon, title, desc], i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 15,
              background: T.card,
              border: `1px solid ${T.grayLine}`,
              borderRadius: 12,
              padding: '20px 22px',
              boxShadow: '0 2px 8px rgba(27,42,74,.05)',
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: 46,
                height: 46,
                borderRadius: 12,
                background: T.tealSoft,
                color: T.teal,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon size={23} />
            </div>
            <div>
              <div style={{ fontWeight: 700, color: T.navy, fontSize: 16, marginBottom: 5 }}>{title}</div>
              <div style={{ fontSize: 13.5, color: T.gray, lineHeight: 1.55 }}>{desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: vm.optimizationTitles.length > 0 ? '1fr 1fr' : '1fr',
          gap: 24,
        }}
      >
        <ChecklistCard title="Priorities achieved" items={['Maintain Lifestyle', 'Manage Income Taxes', 'Leave a Legacy']} />
        {vm.optimizationTitles.length > 0 && (
          <ChecklistCard title="Optimizations applied" items={vm.optimizationTitles} />
        )}
      </div>
    </Stage>
  );
}
